import React, { Fragment } from 'react'
import { Field } from 'formular'
import { useCallback, useEffect, useForceUpdate, useRef } from 'sb-hooks'

import { FieldError, AutosizeTextarea } from 'components/_new/forms'
import type { AutosizeTextareaProps } from 'components/_new/forms'


export type TextareaFieldProps = AutosizeTextareaProps & {
  field: Field<string>
  focusOnInit?: boolean
  onChange?: (value: string) => void
  dataTestId?: string
}

const TextareaField: React.FunctionComponent<TextareaFieldProps> = (props) => {
  const { field, focusOnInit, onChange, onBlur, dataTestId, ...rest } = props

  const ref = useRef<HTMLTextAreaElement>(null)
  const forceUpdate = useForceUpdate()

  useEffect(() => {
    if (focusOnInit) {
      ref.current.focus()
    }

    field.setRef(ref.current)

    field.on('change', forceUpdate)
    field.on('unset', forceUpdate)
    field.on('validate', forceUpdate)

    return () => {
      field.unsetRef()
      field.off('change', forceUpdate)
      field.off('unset', forceUpdate)
      field.off('validate', forceUpdate)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((event) => {
    const { value } = event.target

    field.set(value)

    if (typeof onChange === 'function') {
      onChange(value)
    }
  }, [ field, onChange ])

  const handleBlur = useCallback(async (event) => {
    await field.debounceValidate()

    if (typeof onBlur === 'function') {
      onBlur(event)
    }
  }, [ field, onBlur ])

  const { value, error } = field.state

  return (
    <Fragment>
      <AutosizeTextarea
        {...rest}
        ref={ref}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        data-testid={dataTestId}
      />
      {
        Boolean(error) && (
          <FieldError message={error} />
        )
      }
    </Fragment>
  )
}


export default TextareaField
